import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MaterialReactTable from 'material-react-table';
import { useAxios } from '../../contexts/axios';
import { BACKEND_URL } from '../../configs';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { ExportToCsv } from 'export-to-csv';
import { useAuth } from '../../contexts/auth';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    Tooltip,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import {
    getTeamSelectTextFieldProps,
    normalizeTeamFieldForRow,
    normalizedTeamEditValue,
    teamCellDisplayLabel,
    teamSelectMenuItems,
} from './taggingTeamSelect';

const KAFKA_TOPICS_METADATA_PATH = '/kafka/topics-metadata';

function normalizeListResponse(data) {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.results)) return data.results;
    if (data && Array.isArray(data.data)) return data.data;
    return [];
}

function pickFirstPresent(source, candidates, fallback = '') {
    for (let i = 0; i < candidates.length; i++) {
        const key = candidates[i];
        if (source?.[key] !== undefined && source?.[key] !== null) {
            return source[key];
        }
    }
    return fallback;
}

function parseKafkaMetadataRow(raw) {
    if (!raw || typeof raw !== 'object') return raw;
    const __apiSource = { ...raw };
    const topicName = pickFirstPresent(__apiSource, [
        'topic_name',
        'topic',
        'name',
    ]);
    const clusterName = pickFirstPresent(__apiSource, [
        'cluster_name',
        'cluster',
    ]);
    return {
        ...__apiSource,
        topic_name: String(topicName ?? ''),
        cluster_name: String(clusterName ?? ''),
        team: normalizeTeamFieldForRow(
            String(pickFirstPresent(__apiSource, ['team']) ?? '')
        ),
        vertical: String(pickFirstPresent(__apiSource, ['vertical']) ?? ''),
        application: String(
            pickFirstPresent(__apiSource, ['application']) ?? ''
        ),
        owner: String(pickFirstPresent(__apiSource, ['owner']) ?? ''),
        __apiSource,
    };
}

function isOptionalTagField(columnId) {
    return ['team', 'application', 'owner'].includes(columnId);
}

function kafkaPostPayloadFromForm(values) {
    return {
        topic_name: String(values.topic_name ?? ''),
        cluster_name: String(values.cluster_name ?? ''),
        team: String(values.team ?? ''),
        vertical: String(values.vertical ?? ''),
        application: String(values.application ?? ''),
        owner: String(values.owner ?? ''),
    };
}

function kafkaApiRowForRequest(row) {
    if (row?.__apiSource && typeof row.__apiSource === 'object') {
        return { ...row.__apiSource };
    }
    const { __apiSource, ...rest } = row ?? {};
    return { ...rest };
}

export default function KafkaTagging() {
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [validationErrors, setValidationErrors] = useState({});
    const axios = useAxios();
    const { isAdmin } = useAuth();

    function call(url, data, method) {
        const requestOptions = {
            url: `${BACKEND_URL}${KAFKA_TOPICS_METADATA_PATH}${url}`,
            method: method,
            data: data,
        };
        axios(requestOptions);
    }

    useEffect(() => {
        axios({ url: `${BACKEND_URL}${KAFKA_TOPICS_METADATA_PATH}` })
            .then(function (res) {
                const list = normalizeListResponse(res.data).map(
                    parseKafkaMetadataRow
                );
                setTableData(list);
            })
            .catch(() => {
                setTableData([]);
            });
    }, []);

    const handleCreateNewRow = (values) => {
        if (String(values.topic_name ?? '').trim().length <= 2) {
            alert('Please enter valid topic name, too few characters');
            return;
        }
        if (!String(values.cluster_name ?? '').trim()) {
            alert('Please enter a cluster name');
            return;
        }

        const payload = kafkaPostPayloadFromForm(values);
        const optimistic = parseKafkaMetadataRow(payload);
        tableData.push(optimistic);
        setTableData([...tableData]);
        call('', payload, 'POST');
    };

    const handleSaveRowEdits = async ({ exitEditingMode, row, values }) => {
        if (!Object.keys(validationErrors).length) {
            const merged = parseKafkaMetadataRow({
                ...kafkaApiRowForRequest(row.original),
                team: values.team,
                application: values.application,
                owner: values.owner,
            });
            tableData[row.index] = merged;
            setTableData([...tableData]);
            exitEditingMode();

            call(
                '',
                {
                    ...kafkaApiRowForRequest(row.original),
                    team: values.team,
                    application: values.application,
                    owner: values.owner,
                },
                'PUT'
            );
        }
    };

    const handleDeleteRow = useCallback(
        (row) => {
            if (
                !window.confirm(
                    `Are you sure you want to delete ${row.getValue('topic_name')}`
                )
            ) {
                return;
            }
            const payload = kafkaApiRowForRequest(row.original);
            tableData.splice(row.index, 1);
            setTableData([...tableData]);
            call('', payload, 'DELETE');
        },
        [tableData]
    );

    const getCommonEditTextFieldProps = useCallback(
        (cell) => ({
            error: !!validationErrors[cell.id],
            helperText: validationErrors[cell.id],
            onBlur: (event) => {
                const value = event.target.value;
                const isValid = isOptionalTagField(cell.column.id)
                    ? true
                    : validateRequired(value);
                if (!isValid) {
                    setValidationErrors({
                        ...validationErrors,
                        [cell.id]: `${cell.column.columnDef.header} is required`,
                    });
                } else {
                    delete validationErrors[cell.id];
                    setValidationErrors({
                        ...validationErrors,
                    });
                }
            },
        }),
        [validationErrors]
    );

    const columns = useMemo(
        () => [
            {
                accessorKey: 'topic_name',
                header: 'TOPIC NAME',
                size: 220,
                enableEditing: false,
                muiTableBodyCellEditTextFieldProps: {
                    InputProps: { readOnly: true },
                },
            },
            {
                accessorKey: 'cluster_name',
                header: 'CLUSTER',
                size: 180,
                enableEditing: false,
                muiTableBodyCellEditTextFieldProps: {
                    InputProps: { readOnly: true },
                },
            },
            {
                accessorKey: 'team',
                header: 'TEAM',
                Cell: ({ cell }) => teamCellDisplayLabel(cell.getValue()),
                muiTableBodyCellEditTextFieldProps: ({ cell }) => ({
                    ...getTeamSelectTextFieldProps(),
                    select: true,
                    children: teamSelectMenuItems(),
                    ...getCommonEditTextFieldProps(cell),
                }),
            },
            {
                accessorKey: 'vertical',
                header: 'VERTICAL',
                enableEditing: false,
            },
            {
                accessorKey: 'application',
                header: 'APPLICATION',
                muiTableBodyCellEditTextFieldProps: ({ cell }) => ({
                    ...getCommonEditTextFieldProps(cell),
                }),
            },
            {
                accessorKey: 'owner',
                header: 'OWNER',
                muiTableBodyCellEditTextFieldProps: ({ cell }) => ({
                    ...getCommonEditTextFieldProps(cell),
                }),
            },
        ],
        [getCommonEditTextFieldProps]
    );

    const createFormColumns = useMemo(
        () =>
            columns.filter((c) => c.accessorKey && !c.meta?.skipInCreate),
        [columns]
    );

    const csvOptions = {
        fieldSeparator: ',',
        quoteStrings: '"',
        decimalSeparator: '.',
        showLabels: true,
        useBom: true,
        filename: 'Kafka Topics Tagging ' + new Date().toDateString(),
        useKeysAsHeaders: false,
        headers: [
            'TOPIC NAME',
            'CLUSTER',
            'TEAM',
            'VERTICAL',
            'APPLICATION',
            'OWNER',
        ],
    };

    const csvExporter = new ExportToCsv(csvOptions);

    const handleExportData = () => {
        const data = tableData.map((row) => ({
            topic_name: row.topic_name,
            cluster_name: row.cluster_name,
            team: row.team,
            vertical: row.vertical,
            application: row.application,
            owner: row.owner,
        }));
        csvExporter.generateCsv(data);
    };

    return (
        <>
            <MaterialReactTable
                displayColumnDefOptions={{
                    'mrt-row-actions': {
                        muiTableHeadCellProps: {
                            align: 'center',
                        },
                        size: 120,
                    },
                }}
                columns={columns}
                data={tableData}
                getRowId={(row) => `${row.cluster_name}-${row.topic_name}`}
                editingMode="modal"
                enableColumnOrdering
                enableStickyFooter
                enableStickyHeader
                enableEditing
                enableDensityToggle={false}
                initialState={{
                    columnPinning: {
                        left: ['mrt-row-actions', 'topic_name'],
                    },
                    density: 'compact',
                }}
                enableRowActions
                renderRowActions={({ row, table }) => (
                    <Box sx={{ display: 'flex', gap: '1rem' }}>
                        <Tooltip arrow placement="left" title="Edit">
                            <IconButton
                                disabled={!isAdmin()}
                                onClick={() => table.setEditingRow(row)}
                            >
                                <Edit />
                            </IconButton>
                        </Tooltip>
                        <Tooltip arrow placement="right" title="Delete">
                            <IconButton
                                disabled={!isAdmin()}
                                color="error"
                                onClick={() => handleDeleteRow(row)}
                            >
                                <Delete />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
                positionToolbarAlertBanner="bottom"
                renderTopToolbarCustomActions={() => (
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '1rem',
                            p: '0.5rem',
                            flexWrap: 'wrap',
                        }}
                    >
                        <Button
                            color="success"
                            onClick={() => setCreateModalOpen(true)}
                            variant="contained"
                            disabled={!isAdmin()}
                        >
                            Add New Topic
                        </Button>
                        <Button
                            color="primary"
                            onClick={handleExportData}
                            startIcon={<FileDownloadIcon />}
                            variant="contained"
                        >
                            Export All Data
                        </Button>
                    </Box>
                )}
                onEditingRowSave={handleSaveRowEdits}
            />
            <AddNewTopic
                columns={createFormColumns}
                open={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                onSubmit={handleCreateNewRow}
            />
        </>
    );
}

export const AddNewTopic = ({ open, columns, onClose, onSubmit }) => {
    const [values, setValues] = useState(() =>
        columns.reduce((acc, column) => {
            acc[column.accessorKey ?? ''] = '';
            return acc;
        }, {})
    );
    const handleSubmit = () => {
        onSubmit(values);
        onClose();
    };

    return (
        <Dialog open={open}>
            <DialogTitle textAlign="center">Add Kafka Topic Details</DialogTitle>
            <DialogContent>
                <form onSubmit={(e) => e.preventDefault()}>
                    <Stack
                        sx={{
                            width: '100%',
                            minWidth: { xs: '300px', sm: '360px', md: '400px' },
                            gap: '1.5rem',
                        }}
                    >
                        {columns.map((object) => {
                            if (object.accessorKey === 'team') {
                                return (
                                    <TextField
                                        {...getTeamSelectTextFieldProps()}
                                        key="team"
                                        select
                                        fullWidth
                                        label={object.header}
                                        name="team"
                                        value={normalizedTeamEditValue(
                                            values.team
                                        )}
                                        onChange={(e) =>
                                            setValues({
                                                ...values,
                                                team: e.target.value,
                                            })
                                        }
                                    >
                                        {teamSelectMenuItems()}
                                    </TextField>
                                );
                            }
                            return (
                                <TextField
                                    key={object.accessorKey}
                                    label={object.header}
                                    name={object.accessorKey}
                                    onChange={(e) =>
                                        setValues({
                                            ...values,
                                            [e.target.name]: e.target.value,
                                        })
                                    }
                                />
                            );
                        })}
                    </Stack>
                </form>
            </DialogContent>
            <DialogActions sx={{ p: '1.25rem' }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    color="secondary"
                    onClick={handleSubmit}
                    variant="contained"
                >
                    Add New Topic
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const validateRequired = (value) => !!String(value ?? '').trim().length;
